import { useState, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo, testimonials, animeList, articles } from '@/mocks/anime';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch(
        'https://readdy.ai/api/form/d9pck42ftmtnfcfh0kjg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else if (serverMsg && String(serverMsg).includes('spam')) {
        setFormStatus('error');
        setFormMessage('发送失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormMessage(
          typeof serverMsg === 'string' && serverMsg
            ? serverMsg
            : '发送失败，请稍后重试。'
        );
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: window.location.origin,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '关于我们',
                },
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: siteInfo.name,
              url: window.location.origin,
              description: siteInfo.description,
              email: siteInfo.email,
              telephone: siteInfo.phone,
              foundingDate: String(siteInfo.foundedYear),
              address: {
                '@type': 'PostalAddress',
                addressLocality: '广州',
                addressCountry: 'CN',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: `关于${siteInfo.name}`,
              description: siteInfo.description,
              url: window.location.href,
              isPartOf: {
                '@type': 'WebSite',
                name: siteInfo.name,
                url: window.location.origin,
              },
            },
          ]),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Hero */}
          <section className="mt-6 text-center max-w-3xl mx-auto mb-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于樱花动漫
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              {siteInfo.description}
            </p>
          </section>

          {/* Brand Story */}
          <section className="mb-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                  为动漫而生，因热爱相聚
                </h2>
                <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    樱花动漫创立于{siteInfo.foundedYear}
                    年，由一群热爱动漫文化的年轻人共同发起。我们的初心很简单——打造一个真正属于动漫爱好者的家园，让每一个喜欢动漫的人都能在这里找到归属感。
                  </p>
                  <p>
                    从最初的几个人到如今拥有
                    <strong>{siteInfo.totalUsers.toLocaleString()}+</strong>
                    注册用户的动漫社区，樱花动漫始终坚持以内容品质为核心，为动漫爱好者提供高质量的原创文章、深度分析和精选推荐。
                  </p>
                  <p>
                    我们相信，动漫不仅仅是一种娱乐方式，更是一种文化、一种生活方式。每一部优秀的动漫作品，都承载着创作者的心血和观众的情感。我们希望通过对动漫的深入解读和分享，让更多人感受到动漫的魅力。
                  </p>
                </div>
                <div className="flex items-center gap-6 mt-6">
                  <div>
                    <div className="font-heading text-2xl font-bold text-primary-500">
                      {siteInfo.totalArticles.toLocaleString()}+
                    </div>
                    <div className="text-xs text-foreground-500">原创文章</div>
                  </div>
                  <div>
                    <div className="font-heading text-2xl font-bold text-primary-500">
                      {siteInfo.totalUsers.toLocaleString()}+
                    </div>
                    <div className="text-xs text-foreground-500">注册用户</div>
                  </div>
                  <div>
                    <div className="font-heading text-2xl font-bold text-primary-500">
                      {String(new Date().getFullYear() - siteInfo.foundedYear)}+
                    </div>
                    <div className="text-xs text-foreground-500">年深耕</div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20anime%20studio%20workspace%20with%20shelves%20of%20manga%20and%20anime%20figures%2C%20cherry%20blossom%20decorations%2C%20warm%20golden%20lighting%2C%20creative%20atmosphere%2C%20Japanese%20anime%20aesthetic%2C%20clean%20and%20organized&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="樱花动漫品牌故事"
                  title="樱花动漫品牌故事 - 樱花动漫网"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14 py-10 bg-background-100 rounded-2xl px-4 md:px-8">
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
                用户口碑
              </span>
              <h2 className="font-heading text-2xl font-bold text-foreground-900">
                动漫爱好者都在说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-foreground-900">
                        {t.name}
                      </div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-0.5 mt-3 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                  期待你的声音
                </h2>
                <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
                  无论你是想推荐好看的动漫、提出建议，还是寻求合作，我们都非常乐意倾听。樱花动漫因你而更精彩！
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">电子邮箱</div>
                      <div className="text-sm font-medium text-foreground-800">
                        {siteInfo.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">联系电话</div>
                      <div className="text-sm font-medium text-foreground-800">
                        {siteInfo.phone}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">公司地址</div>
                      <div className="text-sm font-medium text-foreground-800">
                        {siteInfo.address}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">微信公众号</div>
                      <div className="text-sm font-medium text-foreground-800">
                        {siteInfo.wechat}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">
                  在线留言
                </h3>
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-website-alt"
                  />
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      你的名字
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入你的名字"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      电子邮箱
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-subject"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请选择留言主题"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入你的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  {formStatus !== 'idle' && (
                    <div
                      className={`text-sm p-3 rounded-lg ${
                        formStatus === 'success'
                          ? 'bg-secondary-100 text-secondary-800'
                          : formStatus === 'error'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-background-200/70 text-foreground-600'
                      }`}
                    >
                      {formStatus === 'loading' && (
                        <span className="flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          正在发送...
                        </span>
                      )}
                      {formStatus === 'success' && (
                        <span className="flex items-center gap-2">
                          <i className="ri-check-line"></i>
                          {formMessage}
                        </span>
                      )}
                      {formStatus === 'error' && (
                        <span className="flex items-center gap-2">
                          <i className="ri-close-line"></i>
                          {formMessage}
                        </span>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}